import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import NavigationBar from '../../components/ui/NavigationBar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import PaymentElementLibrary from './components/PaymentElementLibrary';
import PaymentFormCanvas from './components/PaymentFormCanvas';
import PaymentSettingsPanel from './components/PaymentSettingsPanel';
import PreviewModal from './components/PreviewModal';
import { useAuth } from '../../contexts/AuthContext';

const PaymentFormBuilder = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  
  const [formElements, setFormElements] = useState([]);
  const [selectedElement, setSelectedElement] = useState(null);
  const [selectedElementId, setSelectedElementId] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [formTitle, setFormTitle] = useState('Payment Form');
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState('');
  const [paymentConfig, setPaymentConfig] = useState({
    processor: 'stripe',
    currency: 'USD',
    mode: 'fixed',
    amount: 0,
    description: '',
    collectBilling: true,
    collectShipping: false,
    enableSubscription: false,
    subscriptionInterval: 'monthly'
  });
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    
    if (!user) {
      navigate('/authentication');
      return;
    }
  }, [user, authLoading, navigate]);

  const handleElementClick = (elementType) => {
    const newElement = {
      id: `element-${Date.now()}-${Math.random()?.toString(36)?.substr(2, 9)}`,
      type: elementType?.type || elementType?.id,
      label: elementType?.name || 'New Element',
      placeholder: elementType?.placeholder || '',
      required: elementType?.required || false,
      ...elementType
    };
    setFormElements([...formElements, newElement]);
  };

  const handleElementSelect = (element) => {
    setSelectedElement(element);
    setSelectedElementId(element?.id);
  };

  const handleUpdateElement = (updatedElement) => {
    setFormElements(formElements?.map(el => 
      el?.id === updatedElement?.id ? updatedElement : el
    ));
    setSelectedElement(updatedElement);
  };

  const handleElementDelete = (elementId) => {
    setFormElements(formElements?.filter(el => el?.id !== elementId));
    if (selectedElementId === elementId) {
      setSelectedElement(null);
      setSelectedElementId(null);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Simulate save operation
      await new Promise(resolve => setTimeout(resolve, 1000));
      setLastSaved(new Date());
    } catch (error) {
      console.error('Error saving payment form:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleTitleEdit = () => {
    setEditedTitle(formTitle);
    setIsEditingTitle(true);
  };

  const handleTitleSave = () => {
    if (editedTitle?.trim()) {
      setFormTitle(editedTitle?.trim());
    }
    setIsEditingTitle(false);
  };

  const handleTitleCancel = () => {
    setEditedTitle('');
    setIsEditingTitle(false);
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Icon name="Loader2" size={48} className="animate-spin text-primary mx-auto" />
          <p className="text-muted-foreground mt-4">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <NavigationBar stats={{}} />
      {/* Toolbar */}
      <div className="bg-card border-b border-border px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            iconName="ArrowLeft"
            onClick={() => navigate('/dashboard')}
          >
            Back
          </Button>
          
          <div className="h-6 w-px bg-border" />
          
          {isEditingTitle ? (
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={editedTitle}
                onChange={(e) => setEditedTitle(e?.target?.value)}
                onKeyDown={(e) => {
                  if (e?.key === 'Enter') handleTitleSave();
                  if (e?.key === 'Escape') handleTitleCancel();
                }}
                className="px-3 py-1 bg-background border border-input rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                autoFocus
              />
              <Button size="sm" variant="ghost" iconName="Check" onClick={handleTitleSave} />
              <Button size="sm" variant="ghost" iconName="X" onClick={handleTitleCancel} />
            </div>
          ) : (
            <div className="flex items-center gap-2 cursor-pointer group" onClick={handleTitleEdit}>
              <h1 className="text-lg font-semibold text-foreground">{formTitle}</h1>
              <Icon name="Edit2" size={16} className="text-muted-foreground opacity-0 group-hover:opacity-100 transition-smooth" />
            </div>
          )}
        </div>

        <div className="flex items-center gap-3">
          {lastSaved && (
            <span className="text-xs text-muted-foreground">
              Saved {new Date(lastSaved)?.toLocaleTimeString()}
            </span>
          )}
          
          <Button
            variant="outline"
            size="sm"
            iconName="Eye"
            onClick={() => setShowPreview(true)}
          >
            Preview
          </Button>
          
          <Button
            size="sm"
            iconName={saving ? "Loader2" : "Save"}
            iconClassName={saving ? "animate-spin" : ""}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>
      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Element Library */}
        <PaymentElementLibrary onElementClick={handleElementClick} />
        
        {/* Canvas */}
        <div className="flex-1 overflow-auto">
          <PaymentFormCanvas
            formElements={formElements}
            onElementSelect={handleElementSelect}
            selectedElementId={selectedElementId}
            onElementDelete={handleElementDelete}
            paymentConfig={paymentConfig}
          />
        </div>
        
        {/* Settings Panel */}
        <PaymentSettingsPanel
          selectedElement={selectedElement}
          onUpdateElement={handleUpdateElement}
          paymentConfig={paymentConfig}
          onUpdatePaymentConfig={setPaymentConfig}
        />
      </div>
      {/* Preview Modal */}
      {showPreview && (
        <PreviewModal
          isOpen={showPreview}
          onClose={() => setShowPreview(false)}
          formElements={formElements}
          formTitle={formTitle}
          paymentConfig={paymentConfig}
        />
      )}
    </div>
  );
};

export default PaymentFormBuilder;